import { CheckCircle2 } from 'app/design/icons/check-circle2';
import { CheckCircle2NotYet } from 'app/design/icons/check-circle2-not-yet';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import React, { useState } from 'react';
import { Pressable } from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import Animated, { interpolate, useAnimatedStyle, useDerivedValue, withTiming } from 'react-native-reanimated';

import { useTranslation } from 'react-i18next';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronDown, IconChevronRight, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

// --- Circular progress ring ---

const RING_SIZE = 56;
const RING_STROKE = 4;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const TRACK_COLOR = theme.colors['surface.inverse.tertiary-neutral'];
const STEP_ICON_BG = theme.colors['surface.brand.secondary-5.subtle'];
const STEP_ICON_SIZE = 36;
const CHECK_ICON_SIZE = 28;

function interpolateProgressColor(pct: number): string {
  const clamped = Math.min(100, Math.max(0, pct));
  // hue: 0° = red, 30° = orange, 110° = green
  const hue = (clamped / 100) * 110;
  return `hsl(${hue.toFixed(1)}, 70%, 45%)`;
}

function CircularProgress({ percentage }: { percentage: number }) {
  const strokeDashoffset = RING_CIRCUMFERENCE * (1 - Math.min(percentage, 100) / 100);
  const activeColor = interpolateProgressColor(percentage);
  return (
    <Column alignItems="center" justifyContent="center" style={{ width: RING_SIZE, height: RING_SIZE }}>
      <Svg width={RING_SIZE} height={RING_SIZE} style={{ position: 'absolute' }}>
        <Circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          stroke={TRACK_COLOR}
          strokeWidth={RING_STROKE}
          fill="none"
        />
        <Circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          stroke={activeColor}
          strokeWidth={RING_STROKE}
          fill="none"
          strokeDasharray={String(RING_CIRCUMFERENCE)}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90, ${String(RING_SIZE / 2)}, ${String(RING_SIZE / 2)})`}
        />
      </Svg>
      <Text fontSize={13} fontWeight="700" color="text.primary">
        {`${Math.round(percentage).toString()}%`}
      </Text>
    </Column>
  );
}

// --- Public types ---

export interface Step {
  /** Emoji or ReactNode icon displayed to the left of the step label */
  icon: React.ReactNode;
  /** Translated label for this step */
  label: string;
  /** Whether the step has been completed */
  completed: boolean;
  /** Optional callback when the step row is pressed */
  onPress?: () => void;
}

interface StepsToCompleteProps {
  /** Ordered list of steps */
  steps: Step[];
  /** Title shown while steps remain */
  title: string;
  /** Subtitle override; defaults to "X sur Y étape(s) complétée(s)" */
  subtitle?: string;
  /** Description shown in the expanded state */
  description?: string;
  /** Title shown when all steps are done */
  completedTitle?: string;
  /** Subtitle shown when all steps are done */
  completedSubtitle?: string;
  /** Called when the user dismisses the completed banner */
  onDismiss?: () => void;
}

export function useHideStepsToComplete() {
  const [closedCompletedProfileBanner, , , completedProfileBannerLoaded] = useStoredValue<boolean>(
    StorageKey.ClosedCompletedProfileBanner,
    false,
  );

  return !completedProfileBannerLoaded || closedCompletedProfileBanner;
}

export function StepsToComplete({
  steps,
  title,
  subtitle,
  description,
  completedTitle,
  completedSubtitle,
  onDismiss,
}: StepsToCompleteProps) {
  const totalCount = steps.length;
  const completedCount = steps.filter((s) => s.completed).length;
  const percentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const isComplete = completedCount === totalCount;
  const [expanded, setExpanded] = useState(!isComplete);

  const [closedCompletedProfileBanner, setClosedCompletedProfileBanner, , completedProfileBannerLoaded] =
    useStoredValue<boolean>(StorageKey.ClosedCompletedProfileBanner, false);
  const hideStepsToComplete = !completedProfileBannerLoaded || closedCompletedProfileBanner;
  const { t } = useTranslation('landing-page');
  // Animation
  const animatedValue = useDerivedValue(() => {
    return withTiming(expanded ? 1 : 0, { duration: 250 });
  }, [expanded]);

  const contentStyle = useAnimatedStyle(() => ({
    opacity: animatedValue.value,
    maxHeight: interpolate(animatedValue.value, [0, 1], [0, 600]),
    overflow: 'hidden' as const,
  }));
  const doDismiss = () => {
    if (onDismiss) {
      onDismiss();
    }
    setClosedCompletedProfileBanner(true);
  };

  const chevronStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${interpolate(animatedValue.value, [0, 1], [0, 180]).toString()}deg` }],
  }));

  if (hideStepsToComplete) {
    return null;
  }

  // --- Completed state ---
  if (isComplete) {
    return (
      <Row
        alignItems="center"
        backgroundColor="surface.tertiary-neutral"
        borderRadius="radius.lg"
        gap="spacing.general.x3"
        paddingHorizontal="spacing.general.x4"
        paddingVertical="spacing.general.x4"
      >
        <CircularProgress percentage={100} />
        <Column flex={1} gap="spacing.general.x0,5">
          <Text fontWeight="700" fontSize={15} color="text.primary">
            {completedTitle ?? title}
          </Text>
          {completedSubtitle ? (
            <Text fontSize={13} color="text.primary">
              {completedSubtitle}
            </Text>
          ) : null}
        </Column>
        {onDismiss ? (
          <Pressable
            accessibilityLabel={t('close', { ns: 'accessibility' })}
            accessibilityRole="button"
            onPress={doDismiss}
            hitSlop={12}
            style={{
              position: 'absolute',
              top: theme.spacing['spacing.general.x2'],
              right: theme.spacing['spacing.general.x2'],
            }}
          >
            <IconX strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['icon.secondary']} />
          </Pressable>
        ) : null}
      </Row>
    );
  }

  // --- In-progress state ---
  const defaultSubtitle = t('stepsToComplete.stepsCompleted', { completed: completedCount, total: totalCount });
  const nextStep = steps.find((step) => !step.completed);

  return (
    <Column
      backgroundColor="surface.brand.secondary-5.subtle"
      borderRadius="radius.lg"
      paddingHorizontal="spacing.general.x4"
      paddingVertical="spacing.general.x4"
    >
      <Pressable onPress={() => setExpanded((v) => !v)}>
        <Row alignItems="center" gap="spacing.general.x3">
          <CircularProgress percentage={percentage} />
          <Column flex={1} gap="spacing.general.x0,5">
            <Text fontWeight="700" fontSize={15} color="text.primary">
              {title}
            </Text>
            <Text fontSize={13} color="text.primary">
              {subtitle ?? defaultSubtitle}
            </Text>
            {nextStep ? (
              <Row
                alignItems="center"
                alignSelf="flex-start"
                backgroundColor="surface.primary-neutral"
                borderRadius="radius.full"
                marginTop="spacing.general.x0,5"
                paddingHorizontal="spacing.general.x2"
                paddingVertical="spacing.general.x0,5"
              >
                <Text fontSize={12} fontWeight="500" color="text.secondary">
                  {t('stepsToComplete.nextStep', { step: nextStep.label })}
                </Text>
              </Row>
            ) : null}
          </Column>
          <Animated.View style={chevronStyle}>
            <IconChevronDown strokeWidth={ICON_STROKE_WIDTH} size={20} color={theme.colors['icon.secondary']} />
          </Animated.View>
        </Row>
      </Pressable>

      <Animated.View style={contentStyle}>
        <Column gap="spacing.general.x4" paddingTop="spacing.general.x4">
          {description ? (
            <Text fontSize={13} color="text.primary">
              {description}
            </Text>
          ) : null}

          <Column gap="spacing.general.x3">
            {steps.map((step) => (
              <StepRow key={step.label} step={step} />
            ))}
          </Column>
        </Column>
      </Animated.View>
    </Column>
  );
}

// --- Step row ---

function StepRow({ step }: { step: Step }) {
  const isActionable = Boolean(step.onPress && !step.completed);
  const content = (
    <Row
      alignItems="center"
      gap="spacing.general.x3"
      paddingHorizontal="spacing.general.x3"
      paddingVertical="spacing.general.x2"
    >
      <Column
        alignItems="center"
        justifyContent="center"
        style={{
          width: STEP_ICON_SIZE,
          height: STEP_ICON_SIZE,
          borderRadius: STEP_ICON_SIZE / 2,
          backgroundColor: STEP_ICON_BG,
        }}
      >
        {typeof step.icon === 'string' ? <Text fontSize={18}>{step.icon}</Text> : step.icon}
      </Column>
      <Text flex={1} fontWeight="600" fontSize={14} color="text.primary">
        {step.label}
      </Text>
      {step.completed ? (
        <CheckCircle2
          width={CHECK_ICON_SIZE}
          height={CHECK_ICON_SIZE}
          color={theme.colors['text.brand']}
          fillColor={theme.colors['surface.tertiary']}
        />
      ) : isActionable ? (
        <Column
          alignItems="center"
          backgroundColor="surface.brand.subtle"
          borderRadius="radius.full"
          justifyContent="center"
          style={{ height: CHECK_ICON_SIZE, width: CHECK_ICON_SIZE }}
        >
          <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['icon.brand']} />
        </Column>
      ) : (
        <CheckCircle2NotYet
          color={theme.colors['surface.inverse.tertiary-neutral']}
          width={CHECK_ICON_SIZE}
          height={CHECK_ICON_SIZE}
        />
      )}
    </Row>
  );

  if (isActionable) {
    return (
      <Pressable
        accessibilityRole="button"
        onPress={step.onPress}
        style={({ pressed }) => ({
          backgroundColor: pressed ? theme.colors['state-layer.press-brand'] : theme.colors['surface.default'],
          borderColor: theme.colors['border.secondary'],
          borderRadius: theme.borderRadii['radius.md'],
          borderWidth: theme.borderWidths['border-width.subtle'],
        })}
      >
        {content}
      </Pressable>
    );
  }

  return (
    <Column
      backgroundColor="surface.default"
      borderColor="border.secondary"
      borderRadius="radius.md"
      borderWidth={theme.borderWidths['border-width.subtle']}
    >
      {content}
    </Column>
  );
}
